import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  LinearProgress,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload,
  Image as ImageIcon,
  VideoLibrary,
  MusicNote,
  Description,
  ArrowUpward,
  ArrowDownward,
  Message,
  Timer,
  Layers,
} from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";
import { useStyles } from "./NodeConfigModalStyles";
import { buildMediaUrl, safeParseItems, validarAntesDeSubir, describirErrorSubida } from "./nodeConfig.helpers";
import { useMediaQuery } from "@material-ui/core";
import { i18n } from "../../../translate/i18n";
import { listPrompts } from "../../../services/prompt.api";
import { uploadFlowBuilderMedia, listFlowBuilders } from "../../../services/flowBuilders.api";
import { listTags } from "../../../services/tags.api";
import { findAllQueues } from "../../../services/queue.api";
import { toast } from "react-toastify";
import HsmTemplateConfig from "./HsmTemplateConfig";
import ConditionBranchesEditor, { nuevaRama } from "./ConditionBranchesEditor";
import { channelOfFlow } from "../../../constants/flowChannels";


// ── Utilidades puras ──────────────────────────────────────────────────────────

/**
 * Convierte una ruta relativa del backend (/public/...) a una URL absoluta.
 * Los blobs (URL.createObjectURL) y URLs absolutas se retornan sin cambios.
 */
const NodeConfigModal = ({ open, onClose, node, onSave, flowId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const [nodeData, setNodeData] = useState({});
  const [tags, setTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [queues, setQueues] = useState([]);
  const [loadingQueues, setLoadingQueues] = useState(false);
  const [flows, setFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const fileInputRef = useRef(null);
  const isMountedRef = useRef(true);
  // refs for content-node item uploads (single hidden input, tracked by index)
  const contentFileInputRef = useRef(null);
  const uploadingItemIndexRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (node) {
      setNodeData(node.data || {});
    } else {
      setNodeData({});
    }
  }, [node]);

  // ✅ Cargar tags cuando se abre el modal para nodos tag/removeTag
  useEffect(() => {
    if (open && (node?.type === "tag" || node?.type === "removeTag")) {
      loadTags();
    }
  }, [open, node]);

  // ✅ Cargar prompts (Agentes IA) cuando se abre el modal para nodo openai
  useEffect(() => {
    if (open && node?.type === "openai") {
      loadPrompts();
    }
  }, [open, node]);

  // ✅ Cargar departamentos (queues) cuando se abre el modal para nodo transferQueue
  useEffect(() => {
    if (open && node?.type === "transferQueue") {
      loadQueues();
    }
  }, [open, node]);

  // ✅ Cargar flujos cuando se abre el modal para nodo jumpToFlow (0419 F1)
  useEffect(() => {
    if (open && node?.type === "jumpToFlow") {
      loadFlows();
    }
  }, [open, node]);

  const loadTags = async () => {
    try {
      if (!isMountedRef.current) return;
      setLoadingTags(true);
      const { data } = await listTags();
      
      // ✅ Asegurar que data sea un array
      const tagsArray = Array.isArray(data) ? data : (Array.isArray(data?.tags) ? data.tags : []);
      
      if (isMountedRef.current) {
        setTags(tagsArray);
      }
    } catch (error) {
      console.error("Error cargando tags:", error);
      if (isMountedRef.current) {
        setTags([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingTags(false);
      }
    }
  };

  const loadPrompts = async () => {
    try {
      if (!isMountedRef.current) return;
      setLoadingPrompts(true);
      const { data } = await listPrompts();
      
      // ✅ Asegurar que data.prompts sea un array
      const promptsArray = Array.isArray(data?.prompts) ? data.prompts : [];
      
      if (isMountedRef.current) {
        setPrompts(promptsArray);
      }
    } catch (error) {
      console.error("Error cargando prompts:", error);
      if (isMountedRef.current) {
        setPrompts([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingPrompts(false);
      }
    }
  };

  const loadQueues = async () => {
    try {
      if (!isMountedRef.current) return;
      setLoadingQueues(true);
      const { data } = await findAllQueues();
      const queuesArray = Array.isArray(data) ? data : (Array.isArray(data?.queues) ? data.queues : []);
      if (isMountedRef.current) {
        setQueues(queuesArray);
      }
    } catch (error) {
      console.error("Error cargando departamentos:", error);
      if (isMountedRef.current) {
        setQueues([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingQueues(false);
      }
    }
  };

  const loadFlows = async () => {
    try {
      if (!isMountedRef.current) return;
      setLoadingFlows(true);
      const { data } = await listFlowBuilders();
      const flowsArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.flows)
        ? data.flows
        : Array.isArray(data?.records)
        ? data.records
        : [];
      if (isMountedRef.current) {
        setFlows(flowsArray);
      }
    } catch (error) {
      console.error("Error cargando flujos:", error);
      if (isMountedRef.current) {
        setFlows([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingFlows(false);
      }
    }
  };

  if (!node || !open) return null;

  // ✅ Obtener nombre del nodo para el título del modal
  const getNodeTypeName = () => {
    const nodeTypeNames = {
      'start': 'Start',
      'text': 'Texto',
      'message': 'Texto',
      'image': 'Imagen',
      'video': 'Video',
      'audio': 'Audio',
      'document': 'Documento',
      'input': 'Input',
      'menu': 'Menú',
      'condition': 'Condición',
      'tag': 'Agregar Tag',
      'removeTag': 'Remover Tag',
      'openai': 'Agente IA',
      'httpRequest': 'HTTP Request',
      'wait': 'Wait',
      'typing': 'Typing',
      'setVariable': 'Set Variable',
      'transferQueue': 'Transferir a departamento',
      'jumpToFlow': 'Saltar a otro flujo',
      'closeTicket': 'Cerrar ticket',
      'fileInput': 'Pedir archivo',
      'content': 'Bloque de Respuesta',
      'hsmTemplate': 'Plantilla HSM',
    };
    return nodeTypeNames[node.type] || node.label || node.type || 'Nodo';
  };

  const handleSave = () => {
    onSave({
      ...node,
      data: nodeData,
    });
    onClose();
  };

  // ✅ Función para subir archivo multimedia
  const handleMediaUpload = async (file, mediaType) => {
    if (!file) return;
    const rechazo = validarAntesDeSubir(file);
    if (rechazo) {
      toast.error(rechazo);
      return;
    }

    try {
      setUploadingMedia(true);
      const formData = new FormData();
      formData.append("media", file);

      const { data } = await uploadFlowBuilderMedia(formData);

      if (data.success) {
        setNodeData({
          ...nodeData,
          mediaUrl: data.mediaUrl,
          fileName: data.fileName,
        });
        toast.success("Archivo subido exitosamente");
      }
    } catch (error) {
      console.error("Error subiendo archivo:", error);
      toast.error(describirErrorSubida(error, file));
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleFileSelect = (e, mediaType) => {
    const file = e.target.files[0];
    if (file) {
      handleMediaUpload(file, mediaType);
    }
    // Reset input para permitir seleccionar el mismo archivo nuevamente
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const renderTextConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        multiline
        rows={4}
        label="Mensaje de texto"
        value={nodeData.text || ""}
        onChange={(e) => setNodeData({ ...nodeData, text: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="Escribe tu mensaje aquí. Puedes usar variables: {{name}}, {{number}}, {{email}}"
      />
      <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
        Variables: {"{{name}}"}, {"{{number}}"}, {"{{email}}"}, {"{{appointmentDate}}"}, {"{{confirmationCode}}"}, {"{{serviceName}}"}, {"{{professionalName}}"}
      </Typography>
    </Box>
  );

  // ✅ Retrocompatibilidad: 'message' usa la misma configuración que 'text'
  const renderMessageConfig = renderTextConfig;

  const renderMediaConfig = (mediaType, label, icon, acceptedTypes) => {
    const hasMedia = !!nodeData.mediaUrl;
    const fileSizeLimit = mediaType === "image" ? "5MB" : mediaType === "video" || mediaType === "audio" ? "16MB" : "100MB";

    return (
      <Box className={classes.tabPanel}>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e, mediaType)}
        />
        
        {uploadingMedia && (
          <Box mb={2}>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
              Subiendo archivo...
            </Typography>
          </Box>
        )}

        {hasMedia ? (
          <Box mb={2} p={2} border="1px solid #e0e0e0" borderRadius={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                {icon}
                <Typography variant="body2" fontWeight="bold">
                  Archivo subido
                </Typography>
              </Box>
              <Chip
                label={nodeData.fileName || "Archivo"}
                size="small"
                color="primary"
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<CloudUpload />}
            >
              Cambiar archivo
            </Button>
          </Box>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<CloudUpload />}
            disabled={uploadingMedia}
            style={{ marginBottom: 16, padding: 12 }}
          >
            Subir {label}
          </Button>
        )}

        {/* Por URL (0422 M3): el motor la descarga una vez y la envía como un archivo subido.
            Es lo que traen los bots importados de Typebot. */}
        <TextField
          fullWidth
          label="…o pega una URL pública"
          value={/^https?:\/\//i.test(nodeData.mediaUrl || "") ? nodeData.mediaUrl : ""}
          onChange={(e) =>
            setNodeData({ ...nodeData, mediaUrl: e.target.value.trim(), fileName: undefined })
          }
          variant="outlined"
          margin="normal"
          placeholder="https://…/imagen.jpg"
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Caption (opcional)"
          value={nodeData.caption || ""}
          onChange={(e) => setNodeData({ ...nodeData, caption: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="Texto descriptivo que aparecerá con el archivo. Puedes usar variables: {{name}}"
        />
        
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
          Tamaño máximo: {fileSizeLimit}. Variables: {"{{name}}"}, {"{{appointmentDate}}"}, {"{{confirmationCode}}"}
        </Typography>
      </Box>
    );
  };

  const renderImageConfig = () => renderMediaConfig(
    "image",
    "Imagen",
    <ImageIcon fontSize="small" />,
    "image/*"
  );

  const renderVideoConfig = () => renderMediaConfig(
    "video",
    "Video",
    <VideoLibrary fontSize="small" />,
    "video/*"
  );

  const renderAudioConfig = () => renderMediaConfig(
    "audio",
    "Audio",
    <MusicNote fontSize="small" />,
    "audio/*"
  );

  const renderDocumentConfig = () => {
    const hasMedia = !!nodeData.mediaUrl;
    return (
      <Box className={classes.tabPanel}>
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e, "document")}
        />
        
        {uploadingMedia && (
          <Box mb={2}>
            <LinearProgress />
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
              Subiendo archivo...
            </Typography>
          </Box>
        )}

        {hasMedia ? (
          <Box mb={2} p={2} border="1px solid #e0e0e0" borderRadius={1}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Description fontSize="small" />
                <Typography variant="body2" fontWeight="bold">
                  Documento subido
                </Typography>
              </Box>
              <Chip
                label={nodeData.fileName || "Documento"}
                size="small"
                color="primary"
              />
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              startIcon={<CloudUpload />}
            >
              Cambiar archivo
            </Button>
          </Box>
        ) : (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => fileInputRef.current?.click()}
            startIcon={<CloudUpload />}
            disabled={uploadingMedia}
            style={{ marginBottom: 16, padding: 12 }}
          >
            Subir Documento
          </Button>
        )}

        <TextField
          fullWidth
          label="Nombre del archivo (opcional)"
          value={nodeData.fileName || ""}
          onChange={(e) => setNodeData({ ...nodeData, fileName: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="documento.pdf"
        />

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Caption (opcional)"
          value={nodeData.caption || ""}
          onChange={(e) => setNodeData({ ...nodeData, caption: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="Texto descriptivo que aparecerá con el documento"
        />
        
        <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
          Tamaño máximo: 100MB. Variables: {"{{name}}"}, {"{{appointmentDate}}"}, {"{{confirmationCode}}"}
        </Typography>
      </Box>
    );
  };

  /**
   * Condición (0422 M5): «Sí / No» de siempre o varias ramas. Al pasar a ramas, la comparación
   * actual se convierte en la primera rama para no perderla.
   */
  const conRamas = Array.isArray(nodeData.branches) && nodeData.branches.length > 0;
  const renderConditionConfig = () => (
    <Box className={classes.tabPanel}>
      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            checked={conRamas}
            onChange={(e) =>
              setNodeData(
                e.target.checked
                  ? { ...nodeData, branches: [nuevaRama(1, nodeData)] }
                  : { ...nodeData, branches: undefined }
              )
            }
          />
        }
        label="Varias ramas (como un menú: gana la primera que se cumple)"
      />
      {conRamas ? (
        <ConditionBranchesEditor
          branches={nodeData.branches}
          onChange={(branches) => setNodeData({ ...nodeData, branches })}
        />
      ) : (
      <>
      <TextField
        fullWidth
        label={i18n.t("flowbuilders.modals.condition.variable")}
        value={nodeData.variable || ""}
        onChange={(e) => setNodeData({ ...nodeData, variable: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="ej: name, hasAppointment, userName"
      />
      <TextField
        fullWidth
        select
        label={i18n.t("flowbuilders.modals.condition.operator")}
        value={nodeData.operator || "equals"}
        onChange={(e) => setNodeData({ ...nodeData, operator: e.target.value })}
        variant="outlined"
        margin="normal"
        SelectProps={{ native: true }}
      >
        <option value="equals">Igual a</option>
        <option value="notEquals">Distinto de</option>
        <option value="contains">Contiene</option>
        <option value="notContains">No contiene</option>
        <option value="greater">Mayor que</option>
        <option value="less">Menor que</option>
        <option value="isNotEmpty">Tiene valor</option>
        <option value="isEmpty">Está vacío</option>
      </TextField>
      <TextField
        fullWidth
        label={i18n.t("flowbuilders.modals.condition.value")}
        value={nodeData.value || ""}
        onChange={(e) => setNodeData({ ...nodeData, value: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="Para hasAppointment: true o false"
      />
      <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 8 }}>
        Variables: name, number, email, ticket, hasAppointment (true/false si tiene cita)
      </Typography>
      </>
      )}
    </Box>
  );

  const renderStartConfig = () => (
    <Box className={classes.tabPanel}>
      <Typography variant="body2" color="textSecondary">
        {i18n.t("flowbuilders.modals.start.description")}
      </Typography>
    </Box>
  );

  /**
   * Input (0422 R3). «Validar respuesta» es la regla que aplica el motor
   * (backend domain/validarRespuesta.ts): si la respuesta no la cumple, el flujo no avanza y se
   * vuelve a pedir con el aviso. Sustituye a «Tipo de Input» y «Placeholder», que el motor nunca
   * leyó: prometían una validación que no existía.
   */
  const VALIDACIONES = [
    { value: "texto", label: "Sin validar (cualquier texto)" },
    { value: "digitos", label: "Solo dígitos (cédula, código…)", rango: "Longitud" },
    { value: "numero", label: "Número", rango: "Valor" },
    { value: "email", label: "Correo electrónico" },
    { value: "telefono", label: "Teléfono" },
    { value: "fecha", label: "Fecha (se guarda aaaa-mm-dd)" },
    { value: "url", label: "Enlace (https://…)" },
  ];
  const renderInputConfig = () => {
    const validacion = nodeData.validation || "texto";
    const regla = VALIDACIONES.find((v) => v.value === validacion) || VALIDACIONES[0];
    const legado = !nodeData.validation && nodeData.inputType && nodeData.inputType !== "text";
    const numeroO = (v) => (v === "" ? undefined : Number(v));
    return (
      <Box className={classes.tabPanel}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Mensaje"
          value={nodeData.message || ""}
          onChange={(e) => setNodeData({ ...nodeData, message: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="Por favor, ingresa tu respuesta:"
        />
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>Validar respuesta</InputLabel>
          <Select
            value={validacion}
            onChange={(e) => {
              const { inputType, placeholder, min, max, maxAttempts, ...resto } = nodeData;
              setNodeData(e.target.value === "texto" ? resto : { ...resto, validation: e.target.value });
            }}
            label="Validar respuesta"
          >
            {VALIDACIONES.map((v) => (
              <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {legado && (
          <Typography variant="caption" color="textSecondary" display="block">
            Este nodo tenía «Tipo: {nodeData.inputType}», que nunca se llegó a validar. Elige arriba si quieres que se valide.
          </Typography>
        )}
        {regla.rango && (
          <Box display="flex" style={{ gap: 8 }}>
            <TextField
              type="number"
              label={`${regla.rango} mínima`}
              value={nodeData.min ?? ""}
              onChange={(e) => setNodeData({ ...nodeData, min: numeroO(e.target.value) })}
              variant="outlined"
              margin="normal"
              fullWidth
            />
            <TextField
              type="number"
              label={`${regla.rango} máxima`}
              value={nodeData.max ?? ""}
              onChange={(e) => setNodeData({ ...nodeData, max: numeroO(e.target.value) })}
              variant="outlined"
              margin="normal"
              fullWidth
            />
          </Box>
        )}
        {validacion !== "texto" && (
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Aviso si no es válida (opcional)"
            value={nodeData.retryMessage || ""}
            onChange={(e) => setNodeData({ ...nodeData, retryMessage: e.target.value })}
            variant="outlined"
            margin="normal"
            helperText="Vacío = un aviso por defecto que dice qué se espera. El flujo no avanza hasta que la respuesta sea válida."
          />
        )}
        {validacion !== "texto" && (
          <TextField
            type="number"
            label="Máximo de intentos (vacío = sin tope)"
            value={nodeData.maxAttempts ?? ""}
            onChange={(e) => {
              const v = e.target.value === "" ? undefined : Math.max(1, Math.min(10, Number(e.target.value)));
              setNodeData({ ...nodeData, maxAttempts: v });
            }}
            inputProps={{ min: 1, max: 10 }}
            variant="outlined"
            margin="normal"
            fullWidth
            helperText="Al agotarlos, el flujo sale por «sin respuesta válida» (conéctala, por ejemplo, a Transferir a departamento). Si no la conectas, sigue preguntando."
          />
        )}
      </Box>
    );
  };

  /**
   * Pedir archivo (0419 P2.2).
   *
   * El texto lo envía ESTE nodo, al revés que el «Input»: la petición ("mándame la foto del
   * comprobante") es inseparable de la espera, y separarlas es cómo el Input acaba mudo.
   */
  const renderFileInputConfig = () => {
    const TIPOS = [
      { value: "image", label: "Imagen" },
      { value: "video", label: "Video" },
      { value: "audio", label: "Audio" },
      { value: "document", label: "Documento" },
    ];
    const admitidos = Array.isArray(nodeData.accept) ? nodeData.accept : [];

    const alternar = (tipo) => {
      const siguiente = admitidos.includes(tipo)
        ? admitidos.filter((t) => t !== tipo)
        : [...admitidos, tipo];
      setNodeData({ ...nodeData, accept: siguiente });
    };

    return (
      <Box className={classes.tabPanel}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Petición al cliente"
          value={nodeData.text || nodeData.message || ""}
          onChange={(e) => setNodeData({ ...nodeData, text: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="Envíame la foto del comprobante de pago 📎"
        />

        <Typography variant="subtitle2" style={{ marginTop: 12 }}>
          Tipos admitidos
        </Typography>
        <Typography variant="caption" display="block" color="textSecondary">
          Sin marcar ninguno se acepta cualquier archivo.
        </Typography>
        <Box display="flex" flexWrap="wrap">
          {TIPOS.map((t) => (
            <FormControlLabel
              key={t.value}
              control={
                <Checkbox
                  checked={admitidos.includes(t.value)}
                  onChange={() => alternar(t.value)}
                  color="primary"
                />
              }
              label={t.label}
            />
          ))}
        </Box>

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Mensaje si no envía archivo (opcional)"
          value={nodeData.retryMessage || ""}
          onChange={(e) => setNodeData({ ...nodeData, retryMessage: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="Necesito el archivo adjunto, no un mensaje escrito."
        />

        <TextField
          fullWidth
          label="Guardar también en la variable (opcional)"
          value={nodeData.variable || ""}
          onChange={(e) => setNodeData({ ...nodeData, variable: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="comprobante"
        />
        <Typography variant="caption" display="block" color="textSecondary">
          La URL queda siempre en {"{{file}}"}; el tipo en {"{{fileType}}"} y el nombre en{" "}
          {"{{fileName}}"}. Esta variable extra es solo por comodidad.
        </Typography>

        <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 8 }}>
          El flujo NO avanza hasta recibir un archivo válido: si el cliente responde escribiendo,
          se le vuelve a pedir.
        </Typography>
      </Box>
    );
  };

  // ── Botones WA nativos ────────────────────────────────────────────────────────
  const renderButtonsConfig = () => {
    const buttons = nodeData.buttons || [];

    const BTN_TYPES = [
      { value: "reply", label: "↩ Quick Reply (rutea el flujo)" },
      { value: "url",   label: "🔗 Abrir URL" },
      { value: "copy",  label: "📋 Copiar texto" },
      { value: "call",  label: "📞 Llamar" },
    ];

    const addButton = () => {
      if (buttons.length >= 3) return;
      setNodeData({ ...nodeData, buttons: [...buttons, { type: "reply", text: "", id: "" }] });
    };

    const removeButton = (index) => {
      setNodeData({ ...nodeData, buttons: buttons.filter((_, i) => i !== index) });
    };

    const updateButton = (index, field, value) => {
      const updated = [...buttons];
      updated[index] = { ...updated[index], [field]: value };
      setNodeData({ ...nodeData, buttons: updated });
    };

    const extraField = (btn, index) => {
      switch (btn.type) {
        case "url":
          return (
            <TextField
              fullWidth size="small" label="URL" variant="outlined"
              value={btn.url || ""}
              onChange={(e) => updateButton(index, "url", e.target.value)}
              placeholder="https://ejemplo.com"
            />
          );
        case "copy":
          return (
            <TextField
              fullWidth size="small" label="Texto a copiar" variant="outlined"
              value={btn.copyText || ""}
              onChange={(e) => updateButton(index, "copyText", e.target.value)}
              placeholder="texto que se copiará"
            />
          );
        case "call":
          return (
            <TextField
              fullWidth size="small" label="Número de teléfono" variant="outlined"
              value={btn.phoneNumber || ""}
              onChange={(e) => updateButton(index, "phoneNumber", e.target.value)}
              placeholder="+51912345678"
            />
          );
        default: // reply
          return (
            <TextField
              fullWidth size="small" label="ID del botón (para ruteo)" variant="outlined"
              value={btn.id || ""}
              onChange={(e) => updateButton(index, "id", e.target.value)}
              placeholder="ej: soporte, ventas"
              helperText="Este ID conecta con el nodo siguiente"
            />
          );
      }
    };

    return (
      <Box className={classes.tabPanel}>
        {/* Mensaje principal */}
        <TextField
          fullWidth multiline rows={2} label="Mensaje" variant="outlined" margin="normal"
          value={nodeData.message || ""}
          onChange={(e) => setNodeData({ ...nodeData, message: e.target.value })}
          placeholder="¿Cómo podemos ayudarte?"
        />
        <TextField
          fullWidth size="small" label="Footer (opcional)" variant="outlined" margin="normal"
          value={nodeData.footer || ""}
          onChange={(e) => setNodeData({ ...nodeData, footer: e.target.value })}
          placeholder="Selecciona una opción"
        />

        {/* Botones */}
        <Box mt={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">
              Botones{" "}
              <span style={{ fontSize: 11, color: "#888" }}>
                ({buttons.length}/3)
              </span>
            </Typography>
            <Button
              size="small" variant="outlined"
              onClick={addButton}
              disabled={buttons.length >= 3}
              style={{ borderColor: "#075e54", color: "#075e54" }}
            >
              + Agregar
            </Button>
          </Box>

          {buttons.length === 0 && (
            <Box p={2} textAlign="center" border="1px dashed #ccc" borderRadius={1}>
              <Typography variant="body2" color="textSecondary">
                Agrega hasta 3 botones. Los "Quick Reply" crean rutas en el flujo.
              </Typography>
            </Box>
          )}

          {buttons.map((btn, index) => (
            <Box
              key={index} mb={1.5} p={1.5}
              border="1px solid"
              borderColor={btn.type === "reply" ? "#075e54" : "#e0e0e0"}
              borderRadius={1}
              style={{ background: btn.type === "reply" ? "rgba(7,94,84,0.04)" : "transparent" }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="caption" style={{ fontWeight: 600 }}>
                  Botón {index + 1}
                </Typography>
                <IconButton size="small" onClick={() => removeButton(index)} color="secondary">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box display="flex" flexDirection="column" gap={8}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={btn.type || "reply"}
                    label="Tipo"
                    onChange={(e) => updateButton(index, "type", e.target.value)}
                  >
                    {BTN_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth size="small" label="Texto del botón" variant="outlined"
                  value={btn.text || ""}
                  onChange={(e) => updateButton(index, "text", e.target.value)}
                  placeholder="Texto visible en WhatsApp"
                  style={{ marginTop: 8 }}
                />
                <Box style={{ marginTop: 8 }}>
                  {extraField(btn, index)}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Nota informativa */}
        <Box mt={2} p={1.5} bgcolor="#f0f7f4" borderRadius={1} border="1px solid #b2dfdb">
          <Typography variant="caption" style={{ color: "#075e54" }}>
            <strong>Botones WA nativos</strong> — Se renderizan como botones tapeables en el teléfono del contacto.
            Los botones <strong>Quick Reply</strong> pausan el flujo y lo continúan por la ruta conectada al ID del botón.
            Los botones <strong>URL / Copiar / Llamar</strong> no crean rutas.
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderMenuConfig = () => {
    const options = nodeData.options || [];

    const addOption = () => {
      setNodeData({
        ...nodeData,
        options: [...options, { text: "", value: "" }],
      });
    };

    const removeOption = (index) => {
      const newOptions = options.filter((_, i) => i !== index);
      setNodeData({ ...nodeData, options: newOptions });
    };

    const updateOption = (index, field, value) => {
      const newOptions = [...options];
      newOptions[index] = { ...newOptions[index], [field]: value };
      setNodeData({ ...nodeData, options: newOptions });
    };

    return (
      <Box className={classes.tabPanel}>
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Mensaje del menú"
          value={nodeData.message || ""}
          onChange={(e) => setNodeData({ ...nodeData, message: e.target.value })}
          variant="outlined"
          margin="normal"
          placeholder="Por favor, selecciona una opción:"
        />
        <Box mt={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">Opciones del menú</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addOption}
              variant="outlined"
            >
              Agregar
            </Button>
          </Box>
          {options.map((option, index) => (
            <Box
              key={index}
              display="flex"
              gap={1}
              alignItems="center"
              mb={1}
              p={1}
              border="1px solid #e0e0e0"
              borderRadius={1}
            >
              <Typography variant="body2" style={{ minWidth: 30 }}>
                {index + 1}.
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Texto"
                value={option.text || ""}
                onChange={(e) => updateOption(index, "text", e.target.value)}
                variant="outlined"
                placeholder="Opción"
              />
              <TextField
                fullWidth
                size="small"
                label="Valor"
                value={option.value || ""}
                onChange={(e) => updateOption(index, "value", e.target.value)}
                variant="outlined"
                placeholder="valor"
              />
              <IconButton
                size="small"
                onClick={() => removeOption(index)}
                color="secondary"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          {options.length === 0 && (
            <Typography variant="body2" color="textSecondary" align="center" py={2}>
              No hay opciones. Haz clic en "Agregar" para crear una.
            </Typography>
          )}
        </Box>
        
        {/* ✅ NUEVO: Configuración de mensaje de error personalizado */}
        <Box mt={3} p={2} border="1px solid #e0e0e0" borderRadius={1} bgcolor="#f5f5f5">
          <FormControlLabel
            control={
              <Checkbox
                checked={nodeData.enableCustomError || false}
                onChange={(e) => {
                  const isEnabled = e.target.checked;
                  // ✅ Si se habilita y no hay mensaje personalizado, establecer mensaje por defecto
                  if (isEnabled && !nodeData.customErrorMessage) {
                    setNodeData({
                      ...nodeData,
                      enableCustomError: true,
                      customErrorMessage: "⚠️ Opción no válida. Tienes {optionsCount} opciones disponibles:\n\n{optionsList}\n\nPor favor, selecciona una opción válida."
                    });
                  } else {
                    setNodeData({ ...nodeData, enableCustomError: isEnabled });
                  }
                }}
                color="primary"
              />
            }
            label={
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Habilitar mensaje de error personalizado
              </Typography>
            }
          />
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginLeft: 32, marginTop: 4 }}>
            Si está deshabilitado, se usará el mensaje por defecto con las opciones disponibles
          </Typography>
          
          {nodeData.enableCustomError && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Mensaje de error personalizado"
              value={nodeData.customErrorMessage || "⚠️ Opción no válida. Tienes {optionsCount} opciones disponibles:\n\n{optionsList}\n\nPor favor, selecciona una opción válida."}
              onChange={(e) => setNodeData({ ...nodeData, customErrorMessage: e.target.value })}
              variant="outlined"
              margin="normal"
              helperText="Puedes usar {optionsCount} para el número de opciones y {optionsList} para la lista de opciones. Puedes modificar este mensaje o dejarlo tal cual."
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderTagConfig = () => (
    <Box className={classes.tabPanel}>
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Tag</InputLabel>
        <Select
          value={nodeData.tagId || ""}
          onChange={(e) => {
            const selectedTagId = e.target.value;
            const selectedTag = tags.find(tag => tag.id === selectedTagId);
            // ✅ Guardar tanto tagId como tagName para mostrar en el nodo visual
            setNodeData({ 
              ...nodeData, 
              tagId: selectedTagId,
              tagName: selectedTag ? selectedTag.name : ""
            });
          }}
          label="Tag"
          disabled={loadingTags}
        >
          {Array.isArray(tags) && tags.map((tag) => (
            <MenuItem key={tag.id} value={tag.id}>
              {tag.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {loadingTags && (
        <Typography variant="body2" color="textSecondary">
          Cargando tags...
        </Typography>
      )}
    </Box>
  );

  const renderTransferQueueConfig = () => (
    <Box className={classes.tabPanel}>
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Departamento</InputLabel>
        <Select
          value={nodeData.queueId ?? ""}
          onChange={(e) => {
            const selectedQueueId = e.target.value;
            const selectedQueue = queues.find(q => q.id === selectedQueueId);
            setNodeData({
              ...nodeData,
              queueId: selectedQueueId,
              queueName: selectedQueue ? selectedQueue.name : ""
            });
          }}
          label="Departamento"
          disabled={loadingQueues}
        >
          {Array.isArray(queues) && queues.map((queue) => (
            <MenuItem key={queue.id} value={queue.id}>
              {queue.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {loadingQueues && (
        <Typography variant="body2" color="textSecondary">
          Cargando departamentos...
        </Typography>
      )}
      <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 8 }}>
        El ticket se transferirá a este departamento (ej. Citas, Soporte).
      </Typography>
    </Box>
  );

  /**
   * Cerrar ticket (0422 M2). El aviso es opcional: sin él, cierra en silencio y la conexión
   * envía su despedida si la tiene configurada.
   */
  const renderCloseTicketConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        multiline
        rows={4}
        label="Aviso antes de cerrar (opcional)"
        value={nodeData.message || ""}
        onChange={(e) => setNodeData({ ...nodeData, message: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="Tu consulta quedó resuelta 😊 Si necesitas algo más, escríbenos."
        helperText="Admite variables: {{name}}, {{ticket}}… Lo que conectes después no se ejecuta: el ticket queda cerrado."
      />
    </Box>
  );

  /**
   * Salto a otro flujo (0419 F1). El flujo ACTUAL se excluye de la lista: un flujo que salta a
   * sí mismo entra en bucle, y el motor lo cortaría al décimo salto sin que nadie lo pidiera.
   */
  const renderJumpToFlowConfig = () => {
    const flujoActual = Number(flowId ?? 0);
    // Y solo flujos del MISMO canal (0438 Parte VII): un salto a otro canal llevaría nodos que allí no existen.
    const canalActual = channelOfFlow((Array.isArray(flows) ? flows.find((f) => Number(f.id) === flujoActual) : null)?.provider);
    const candidatos = Array.isArray(flows)
      ? flows.filter((f) => (!flujoActual || Number(f.id) !== flujoActual) && channelOfFlow(f.provider) === canalActual)
      : [];

    return (
      <Box className={classes.tabPanel}>
        <FormControl fullWidth margin="normal" variant="outlined">
          <InputLabel>Flujo destino</InputLabel>
          <Select
            value={nodeData.flowId ?? ""}
            onChange={(e) => {
              const seleccionado = e.target.value;
              const flujo = candidatos.find((f) => f.id === seleccionado);
              setNodeData({
                ...nodeData,
                flowId: seleccionado,
                flowName: flujo ? flujo.name : ""
              });
            }}
            label="Flujo destino"
            disabled={loadingFlows}
          >
            {candidatos.map((flujo) => (
              <MenuItem key={flujo.id} value={flujo.id}>
                {flujo.name}
                {flujo.active === false ? " (desactivado)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loadingFlows && (
          <Typography variant="body2" color="textSecondary">
            Cargando flujos...
          </Typography>
        )}
        <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 8 }}>
          La conversación continúa en el flujo elegido. Este lienzo termina aquí: lo que conectes
          después de este nodo no se ejecutará. Para volver, pon otro nodo de salto en el flujo
          destino apuntando a este.
        </Typography>
      </Box>
    );
  };

  const renderRemoveTagConfig = () => (
    <Box className={classes.tabPanel}>
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Tag a Remover</InputLabel>
        <Select
          value={nodeData.tagId || ""}
          onChange={(e) => {
            const selectedTagId = e.target.value;
            const selectedTag = tags.find(tag => tag.id === selectedTagId);
            // ✅ Guardar tanto tagId como tagName para mostrar en el nodo visual
            setNodeData({ 
              ...nodeData, 
              tagId: selectedTagId,
              tagName: selectedTag ? selectedTag.name : ""
            });
          }}
          label="Tag a Remover"
          disabled={loadingTags}
        >
          {Array.isArray(tags) && tags.map((tag) => (
            <MenuItem key={tag.id} value={tag.id}>
              {tag.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {loadingTags && (
        <Typography variant="body2" color="textSecondary">
          Cargando tags...
        </Typography>
      )}
    </Box>
  );

  const renderSetVariableConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        label="Nombre de Variable"
        value={nodeData.key || nodeData.variable || ""}
        onChange={(e) => setNodeData({ ...nodeData, key: e.target.value, variable: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="ej: nombreUsuario, respuesta, producto"
        helperText="Nombre de la variable (sin espacios, usar camelCase o snake_case)"
      />
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Valor"
        value={nodeData.value ?? ""}
        onChange={(e) => setNodeData({ ...nodeData, value: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="Valor a guardar o {{variable}} para usar otras variables"
        helperText="Puedes usar variables del sistema: {{name}}, {{input}}, {{openaiResponse}}, etc."
      />
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
        <strong>Ejemplos:</strong><br/>
        • Valor fijo: "Juan"<br/>
        • Usar variable del sistema: {"{{name}}"}<br/>
        • Usar respuesta de Input: {"{{input}}"}<br/>
        • Combinar: {"Hola {{name}}, tu respuesta fue: {{input}}"}
      </Typography>
    </Box>
  );

  const renderOpenAIConfig = () => (
    <Box className={classes.tabPanel}>
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Agente IA</InputLabel>
        <Select
          value={nodeData.promptId || ""}
          onChange={(e) => {
            const selectedPrompt = prompts.find(p => p.id === e.target.value);
            setNodeData({ 
              ...nodeData, 
              promptId: e.target.value,
              promptName: selectedPrompt?.name || ""
            });
          }}
          label="Agente IA"
          disabled={loadingPrompts}
        >
          {loadingPrompts ? (
            <MenuItem disabled>Cargando agentes...</MenuItem>
          ) : prompts.length === 0 ? (
            <MenuItem disabled>No hay agentes disponibles</MenuItem>
          ) : (
            prompts.map((prompt) => (
              <MenuItem key={prompt.id} value={prompt.id}>
                {prompt.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      {nodeData.promptId && (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, marginBottom: 8 }}>
          ✅ Agente IA seleccionado: <strong>{nodeData.promptName || prompts.find(p => p.id === nodeData.promptId)?.name}</strong>
        </Typography>
      )}
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 16, padding: 8, backgroundColor: "#f5f5f5", borderRadius: 4 }}>
        💡 <strong>Nota:</strong> Para crear un nuevo Agente IA, ve a la página <strong>"AGENTES IA"</strong> en el menú principal.
      </Typography>
    </Box>
  );

  const renderHTTPRequestConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        label="URL"
        value={nodeData.url || ""}
        onChange={(e) => setNodeData({ ...nodeData, url: e.target.value })}
        variant="outlined"
        margin="normal"
        placeholder="https://api.example.com/endpoint"
      />
      <FormControl fullWidth margin="normal" variant="outlined">
        <InputLabel>Método HTTP</InputLabel>
        <Select
          value={nodeData.method || "GET"}
          onChange={(e) => setNodeData({ ...nodeData, method: e.target.value })}
          label="Método HTTP"
        >
          <MenuItem value="GET">GET</MenuItem>
          <MenuItem value="POST">POST</MenuItem>
          <MenuItem value="PUT">PUT</MenuItem>
          <MenuItem value="PATCH">PATCH</MenuItem>
          <MenuItem value="DELETE">DELETE</MenuItem>
        </Select>
      </FormControl>
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Headers (JSON)"
        value={nodeData.headers ? JSON.stringify(nodeData.headers, null, 2) : ""}
        onChange={(e) => {
          try {
            const headers = e.target.value ? JSON.parse(e.target.value) : {};
            setNodeData({ ...nodeData, headers });
          } catch (error) {
            // Ignorar errores de parsing mientras se escribe
          }
        }}
        variant="outlined"
        margin="normal"
        placeholder='{"Content-Type": "application/json"}'
      />
      {(nodeData.method === "POST" || nodeData.method === "PUT" || nodeData.method === "PATCH") && (
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Body (JSON)"
          value={
            !nodeData.body
              ? ""
              : typeof nodeData.body === "string"
              ? nodeData.body
              : JSON.stringify(nodeData.body, null, 2)
          }
          onChange={(e) => {
            try {
              const body = e.target.value ? JSON.parse(e.target.value) : {};
              setNodeData({ ...nodeData, body });
            } catch (error) {
              // Ignorar errores de parsing mientras se escribe
            }
          }}
          variant="outlined"
          margin="normal"
          placeholder='{"key": "value"}'
        />
      )}
      <TextField
        fullWidth
        label="Guardar respuesta en variable"
        value={nodeData.responseVariable || ""}
        onChange={(e) => setNodeData({ ...nodeData, responseVariable: e.target.value.trim() })}
        variant="outlined"
        margin="normal"
        placeholder="mensaje_final"
        helperText="Cada campo de la respuesta JSON queda también como {{campo}}. Con variable, la respuesta no se envía al cliente: muéstrala con un Texto."
      />
    </Box>
  );

  const renderWaitConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        type="number"
        label="Delay (segundos)"
        value={nodeData.seconds ?? (nodeData.ms ? Math.round(Number(nodeData.ms) / 1000) : 1)}
        onChange={(e) => {
          const seconds = Math.max(0, Number(e.target.value) || 0);
          setNodeData({ ...nodeData, seconds, ms: seconds * 1000 });
        }}
        variant="outlined"
        margin="normal"
        inputProps={{ min: 0, max: 60, step: 1 }}
        helperText="Recomendado: 1–3s para evitar envíos instantáneos"
      />
    </Box>
  );

  const renderTypingConfig = () => (
    <Box className={classes.tabPanel}>
      <TextField
        fullWidth
        type="number"
        label="Typing (segundos)"
        value={nodeData.seconds ?? (nodeData.ms ? Math.round(Number(nodeData.ms) / 1000) : 1)}
        onChange={(e) => {
          const seconds = Math.max(0, Number(e.target.value) || 0);
          setNodeData({ ...nodeData, seconds, ms: seconds * 1000 });
        }}
        variant="outlined"
        margin="normal"
        inputProps={{ min: 0, max: 10, step: 1 }}
        helperText="Simula 'escribiendo...' antes de enviar el siguiente mensaje"
      />
    </Box>
  );

  // ── Content Node handlers ──────────────────────────────────────────────────

  const handleItemUploadClick = (itemIndex, kind) => {
    if (!contentFileInputRef.current) return;
    const acceptMap = { image: "image/*", audio: "audio/*", video: "video/*", document: "*" };
    contentFileInputRef.current.accept = acceptMap[kind] || "*";
    uploadingItemIndexRef.current = itemIndex;
    contentFileInputRef.current.click();
  };

  const handleContentItemFileChange = async (e) => {
    const file = e.target.files[0];
    if (contentFileInputRef.current) contentFileInputRef.current.value = "";
    const itemIndex = uploadingItemIndexRef.current;
    uploadingItemIndexRef.current = null;
    if (!file || itemIndex === null) return;
    const rechazo = validarAntesDeSubir(file);
    if (rechazo) {
      toast.error(rechazo);
      return;
    }

    // ── 1. Preview inmediato con blob local ───────────────────────────────────
    const blobUrl = URL.createObjectURL(file);
    setNodeData((prev) => {
      const items = [...(prev.items || [])];
      items[itemIndex] = {
        ...items[itemIndex],
        previewUrl: blobUrl,   // URL temporal — visible de inmediato
        fileName: file.name,   // nombre visible mientras sube
        isUploading: true,
      };
      return { ...prev, items };
    });

    try {
      setUploadingMedia(true);
      const formData = new FormData();
      formData.append("media", file);
      const { data } = await uploadFlowBuilderMedia(formData);

      if (data.success) {
        // ── 2. Reemplazar blob con URL permanente del backend ─────────────────
        setNodeData((prev) => {
          const items = [...(prev.items || [])];
          // Liberar memoria del blob anterior
          if (items[itemIndex]?.previewUrl?.startsWith("blob:")) {
            URL.revokeObjectURL(items[itemIndex].previewUrl);
          }
          items[itemIndex] = {
            ...items[itemIndex],
            mediaUrl: data.mediaUrl,     // ruta del servidor (/public/...)
            fileName: data.fileName,
            previewUrl: null,            // ya no se necesita el blob
            isUploading: false,
          };
          return { ...prev, items };
        });
        toast.success("Archivo subido exitosamente");
      }
    } catch (error) {
      // ── 3. Revertir si falla ──────────────────────────────────────────────
      setNodeData((prev) => {
        const items = [...(prev.items || [])];
        if (items[itemIndex]?.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(items[itemIndex].previewUrl);
        }
        items[itemIndex] = {
          ...items[itemIndex],
          previewUrl: null,
          isUploading: false,
        };
        return { ...prev, items };
      });
      toast.error(describirErrorSubida(error, file));
    } finally {
      setUploadingMedia(false);
    }
  };

  // ── Content Node renderer ──────────────────────────────────────────────────

  const CONTENT_ITEM_META = {
    text:     { label: "Texto",      color: "#2196F3", Icon: Message },
    image:    { label: "Imagen",     color: "#4CAF50", Icon: ImageIcon },
    audio:    { label: "Audio",      color: "#9C27B0", Icon: MusicNote },
    video:    { label: "Video",      color: "#FF5722", Icon: VideoLibrary },
    document: { label: "Documento",  color: "#607D8B", Icon: Description },
    interval: { label: "Intervalo",  color: "#78909C", Icon: Timer },
  };

  const renderContentItemConfig = (item, index, updateItem) => {
    switch (item.kind) {

      case "text":
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Mensaje de texto"
            value={item.text || ""}
            onChange={(e) => updateItem(index, "text", e.target.value)}
            variant="outlined"
            size="small"
            placeholder="Escribe el mensaje. Variables: {{name}}, {{number}}"
          />
        );

      case "image": {
        // previewUrl = blob local inmediato; mediaUrl = URL servidor ya subida
        const imgSrc = item.previewUrl || buildMediaUrl(item.mediaUrl);
        const hasImage = Boolean(imgSrc);
        return (
          <Box>
            {hasImage && (
              <Box className={classes.previewImageBox}>
                <img
                  src={imgSrc}
                  alt={item.fileName || "preview"}
                  className={classes.previewImage}
                  onError={(e) => {
                    e.target.closest
                      ? (e.target.closest(".previewImageBox") || e.target).style.display = "none"
                      : (e.target.style.display = "none");
                  }}
                />
              </Box>
            )}
            {item.isUploading && <LinearProgress style={{ marginBottom: 6 }} />}
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<CloudUpload />}
              onClick={() => handleItemUploadClick(index, "image")}
              disabled={uploadingMedia}
              style={{ marginBottom: 8 }}
            >
              {hasImage ? "Cambiar imagen" : "Subir imagen"}
            </Button>
            <TextField
              fullWidth
              size="small"
              label="Caption (opcional)"
              value={item.caption || ""}
              onChange={(e) => updateItem(index, "caption", e.target.value)}
              variant="outlined"
              placeholder="Texto que acompaña la imagen"
            />
          </Box>
        );
      }

      case "video": {
        const videoSrc = item.previewUrl || buildMediaUrl(item.mediaUrl);
        const hasVideo = Boolean(videoSrc);
        return (
          <Box>
            {hasVideo && (
              <video
                key={videoSrc}
                src={videoSrc}
                controls
                className={classes.previewVideo}
              />
            )}
            {item.isUploading && <LinearProgress style={{ marginBottom: 6 }} />}
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<CloudUpload />}
              onClick={() => handleItemUploadClick(index, "video")}
              disabled={uploadingMedia}
              style={{ marginBottom: 8 }}
            >
              {hasVideo ? "Cambiar video" : "Subir video"}
            </Button>
            <TextField
              fullWidth
              size="small"
              label="Caption (opcional)"
              value={item.caption || ""}
              onChange={(e) => updateItem(index, "caption", e.target.value)}
              variant="outlined"
              placeholder="Texto que acompaña el video"
            />
          </Box>
        );
      }

      case "audio": {
        const audioSrc = item.previewUrl || buildMediaUrl(item.mediaUrl);
        const hasAudio = Boolean(audioSrc);
        return (
          <Box>
            {hasAudio && (
              <audio
                key={audioSrc}
                src={audioSrc}
                controls
                className={classes.previewAudio}
              />
            )}
            {item.isUploading && <LinearProgress style={{ marginBottom: 6 }} />}
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<CloudUpload />}
              onClick={() => handleItemUploadClick(index, "audio")}
              disabled={uploadingMedia}
              style={{ marginBottom: 8 }}
            >
              {hasAudio ? "Cambiar audio" : "Subir audio"}
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={item.isVoice || false}
                  onChange={(e) => updateItem(index, "isVoice", e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="caption">Enviar como nota de voz</Typography>}
            />
          </Box>
        );
      }

      case "document": {
        const hasDoc = Boolean(item.mediaUrl || item.previewUrl);
        return (
          <Box>
            {hasDoc ? (
              <Box className={classes.previewDocument}>
                <Description style={{ color: "#607D8B", fontSize: 30, flexShrink: 0 }} />
                <Box flex={1} overflow="hidden">
                  <Typography variant="body2" noWrap style={{ fontWeight: 600 }}>
                    {item.fileName || "Documento"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {item.isUploading ? "Subiendo…" : "Archivo listo"}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloudUpload />}
                  onClick={() => handleItemUploadClick(index, "document")}
                  disabled={uploadingMedia}
                  style={{ flexShrink: 0 }}
                >
                  Cambiar
                </Button>
              </Box>
            ) : (
              <Button
                fullWidth
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<CloudUpload />}
                onClick={() => handleItemUploadClick(index, "document")}
                disabled={uploadingMedia}
                style={{ marginBottom: 8 }}
              >
                Subir documento
              </Button>
            )}
            {item.isUploading && <LinearProgress style={{ marginBottom: 6 }} />}
            <TextField
              fullWidth
              size="small"
              label="Caption (opcional)"
              value={item.caption || ""}
              onChange={(e) => updateItem(index, "caption", e.target.value)}
              variant="outlined"
              placeholder="Texto que acompaña el documento"
              style={{ marginTop: 8 }}
            />
          </Box>
        );
      }

      case "interval":
        return (
          <TextField
            fullWidth
            type="number"
            label="Segundos de espera"
            value={item.seconds ?? 1}
            onChange={(e) =>
              updateItem(index, "seconds", Math.max(0, Number(e.target.value) || 1))
            }
            variant="outlined"
            size="small"
            inputProps={{ min: 0, max: 60, step: 1 }}
            helperText="Pausa antes del siguiente elemento (recomendado: 1–3s)"
          />
        );

      default:
        return null;
    }
  };

  const renderContentConfig = () => {
    const items = safeParseItems(nodeData.items);

    const addItem = (kind) => {
      const newItem = {
        id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        kind,
        ...(kind === "interval" ? { seconds: 1 } : {}),
      };
      setNodeData((prev) => ({ ...prev, items: [...(prev.items || []), newItem] }));
    };

    const removeItem = (index) => {
      setNodeData((prev) => ({
        ...prev,
        items: (prev.items || []).filter((_, i) => i !== index),
      }));
    };

    const moveItem = (index, dir) => {
      setNodeData((prev) => {
        const newItems = [...(prev.items || [])];
        const target = index + dir;
        if (target < 0 || target >= newItems.length) return prev;
        [newItems[index], newItems[target]] = [newItems[target], newItems[index]];
        return { ...prev, items: newItems };
      });
    };

    const updateItem = (index, field, value) => {
      setNodeData((prev) => {
        const newItems = [...(prev.items || [])];
        newItems[index] = { ...newItems[index], [field]: value };
        return { ...prev, items: newItems };
      });
    };

    return (
      <Box>
        {/* Block label */}
        <TextField
          fullWidth
          label="Nombre del bloque (opcional)"
          value={nodeData.label || ""}
          onChange={(e) => setNodeData((prev) => ({ ...prev, label: e.target.value }))}
          variant="outlined"
          margin="normal"
          placeholder="ej: Bienvenida, Presentación de producto"
        />

        {/* Hidden file input shared by all media items */}
        <input
          ref={contentFileInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleContentItemFileChange}
        />

        {/* ── Items scrollable ─────────────────────────────────────────────── */}
        <Box className={classes.contentItemsScroller}>
          {items.length === 0 && (
            <Box
              p={2}
              textAlign="center"
              border="1px dashed #bdbdbd"
              borderRadius={4}
            >
              <Typography variant="body2" color="textSecondary">
                Sin elementos. Usa los botones de abajo para agregar contenido.
              </Typography>
            </Box>
          )}

          {items.map((item, index) => {
            const meta = CONTENT_ITEM_META[item.kind] || CONTENT_ITEM_META.text;
            const { label, color, Icon } = meta;
            return (
              <Box
                key={item.id || index}
                border="1px solid #e0e0e0"
                borderRadius={4}
                mb={1.5}
                overflow="hidden"
              >
                {/* Item header */}
                <Box
                  display="flex"
                  alignItems="center"
                  px={1.5}
                  py={0.75}
                  style={{
                    backgroundColor:
                      theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Icon style={{ color, fontSize: 18, marginRight: 8, flexShrink: 0 }} />
                  <Typography
                    variant="subtitle2"
                    style={{ fontWeight: 600, flex: 1, fontSize: 13 }}
                  >
                    {label}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => moveItem(index, -1)}
                    disabled={index === 0}
                    title="Mover arriba"
                  >
                    <ArrowUpward fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveItem(index, 1)}
                    disabled={index === items.length - 1}
                    title="Mover abajo"
                  >
                    <ArrowDownward fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => removeItem(index)}
                    style={{ color: "#f44336" }}
                    title="Eliminar"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Item config form */}
                <Box px={1.5} py={1.25}>
                  {renderContentItemConfig(item, index, updateItem)}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ── Pie fijo: botones Agregar ────────────────────────────────────── */}
        <Box
          pt={1}
          mt={0.5}
          style={{
            borderTop: `1px solid ${theme.palette.divider}`,
            position: "sticky",
            bottom: 0,
            backgroundColor: theme.palette.background.paper,
            zIndex: 1,
          }}
        >
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            style={{ marginBottom: 6, fontWeight: 600 }}
          >
            Agregar elemento:
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 6, paddingBottom: 4 }}>
            {Object.entries(CONTENT_ITEM_META).map(([kind, { label, color, Icon }]) => (
              <Button
                key={kind}
                size="small"
                variant="outlined"
                startIcon={<Icon style={{ fontSize: 15, color }} />}
                onClick={() => addItem(kind)}
                style={{ borderColor: color, color, fontSize: 12, padding: "3px 10px" }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderConfig = () => {
    switch (node.type) {
      // ✅ BUBBLES: Nodos de contenido (Arquitectura Typebot)
      case "text":
      case "message": // ✅ Retrocompatibilidad
        return renderTextConfig();
      case "image":
        return renderImageConfig();
      case "video":
        return renderVideoConfig();
      case "audio":
        return renderAudioConfig();
      case "document":
        return renderDocumentConfig();
      
      // ✅ INPUTS: Nodos de interacción
      case "input":
        return renderInputConfig();
      case "menu":
        return renderMenuConfig();
      case "buttons":
        return renderButtonsConfig();
      case "hsmTemplate":
        return (
          <Box className={classes.tabPanel}>
            <HsmTemplateConfig nodeData={nodeData} setNodeData={setNodeData} />
          </Box>
        );

      // ✅ LOGIC: Nodos de lógica
      case "condition":
        return renderConditionConfig();
      case "tag":
        return renderTagConfig();
      case "removeTag":
        return renderRemoveTagConfig();
      case "openai":
        return renderOpenAIConfig();
      case "httpRequest":
        return renderHTTPRequestConfig();
      case "wait":
        return renderWaitConfig();
      case "typing":
        return renderTypingConfig();
      case "setVariable":
        return renderSetVariableConfig();
      case "transferQueue":
        return renderTransferQueueConfig();
      case "jumpToFlow":
        return renderJumpToFlowConfig();
      case "closeTicket":
        return renderCloseTicketConfig();
      case "fileInput":
        return renderFileInputConfig();
      
      // ✅ CONTENEDOR: Bloque de Respuesta multi-contenido
      case "content":
        return renderContentConfig();

      // ✅ CONTROL: Nodos de control
      case "start":
        return renderStartConfig();

      default:
        return (
          <Box className={classes.tabPanel}>
            <Typography variant="body2">
              {i18n.t("flowbuilders.modals.noConfig")}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={node?.type === "content" ? "md" : "sm"}
      fullWidth
      fullScreen={isXsScreen}
      scroll="paper"
      PaperProps={{
        style: {
          maxHeight: isXsScreen ? "100vh" : "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle style={{ flexShrink: 0 }}>
        Configurar {getNodeTypeName()}
      </DialogTitle>
      <DialogContent
        className={classes.dialogContent}
        style={{ flex: "1 1 auto", overflowY: "auto" }}
      >
        {renderConfig()}
      </DialogContent>
      <DialogActions style={{ flexShrink: 0 }}>
        <Button onClick={onClose} color="default">
          {i18n.t("flowbuilders.modals.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {i18n.t("flowbuilders.modals.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NodeConfigModal;
